'use client';
import { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { usePathname } from 'next/navigation';

export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isHeaderVisible, setIsHeaderVisible] = useState(true);
  const [prevScrollY, setPrevScrollY] = useState(0);

  const pathname = usePathname();
  const isHomePage = pathname === '/';
  const scrollTimeoutRef = useRef(null);

  // 스크롤 감지 및 헤더 표시/숨김 로직
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const isAtTop = currentScrollY < 50;

      setIsScrolled(!isAtTop);

      // 모바일 메뉴가 열려있을 때는 스크롤 로직 무시
      if (isMobileMenuOpen) return;

      if (isAtTop) {
        setIsHeaderVisible(true);
      } else {
        // 스크롤 방향에 따라 헤더 표시 여부 결정
        if (currentScrollY > prevScrollY && currentScrollY > 100) {
          setIsHeaderVisible(false); // 아래로 스크롤 시 숨김
        } else if (currentScrollY < prevScrollY) {
          setIsHeaderVisible(true); // 위로 스크롤 시 표시
        }
      }

      setPrevScrollY(currentScrollY);

      // 스크롤 멈춤 감지 (자동 숨김 기능)
      if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);

      if (!isAtTop && !isMobileMenuOpen) {
        scrollTimeoutRef.current = setTimeout(() => {
          setIsHeaderVisible(false);
        }, 2000); // 2초 후 숨김 (시간을 조금 늘림)
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
    };
  }, [prevScrollY, isMobileMenuOpen]);

  // 모바일 메뉴 토글 시 바디 스크롤 막기
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
  }, [isMobileMenuOpen]);

  // 모바일 메뉴 토글
  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
    // 메뉴를 열 때 헤더를 강제로 보이게 함
    if (!isMobileMenuOpen) setIsHeaderVisible(true);
  };

  // --- 스타일 로직 ---

  // 메뉴가 열려있으면 무조건 검정 텍스트 (흰색 배경 위)
  // 홈 페이지이고 스크롤 안 되었을 때만 흰색 텍스트
  const isTransparentState = isHomePage && !isScrolled && !isMobileMenuOpen;

  const currentTextColor = isTransparentState ? 'text-white' : 'text-gray-900';
  const logoSrc = isTransparentState
    ? '/images/KDIDARTSCI_White.svg'
    : '/images/KDIDARTSCI.svg'; // 검정 로고

  // 헤더 배경
  const getHeaderBg = () => {
    if (isMobileMenuOpen) return 'bg-white'; // 메뉴 열리면 완전 흰색
    if (isHomePage && !isScrolled) return 'bg-transparent';
    return 'bg-white/90 backdrop-blur-md ';
  };

  return (
    <>
      <header
        className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${getHeaderBg()} ${
          isHeaderVisible ? 'translate-y-0' : '-translate-y-full'
        }`}
      >
        <div className="mx-auto px-4 sm:px-6 lg:px-8 ">
          <div className="flex items-center justify-between h-16 lg:h-20">
            {/* 로고 영역 */}
            <div className="relative z-50 flex-shrink-0">
              <Link href="/" onClick={() => setIsMobileMenuOpen(false)}>
                <div className="relative w-[120px] h-[40px]">
                  <Image
                    src={logoSrc}
                    alt="KD darts Logo"
                    fill
                    style={{ objectFit: 'contain', objectPosition: 'left' }}
                    priority
                  />
                </div>
              </Link>
            </div>

            {/* 데스크톱 메뉴 */}
            <nav className="hidden lg:flex items-center space-x-8">
              {[
                { name: '회사 소개', href: '/company' },
                { name: '핵심 보유 기술', href: '/technologies' },
                // { name: '제품 소개', href: '/solution' },
                { name: '투자정보', href: '/board' },
                { name: 'Contact Us', href: '/contactus' },
              ].map((item) => (
                <Link
                  key={item.name}
                  href={item.href}
                  className={`text-sm font-semibold tracking-wide transition-all duration-200 hover:opacity-70 ${currentTextColor} relative group`}
                >
                  {item.name}
                  {/* 호버 시 밑줄 애니메이션 */}
                  <span
                    className={`absolute -bottom-1 left-0 w-0 h-0.5 transition-all duration-300 group-hover:w-full ${isTransparentState ? 'bg-white' : 'bg-blue-600'}`}
                  ></span>
                </Link>
              ))}
            </nav>

            {/* 모바일 햄버거 버튼 */}
            <div className="lg:hidden relative z-50">
              <button
                onClick={toggleMobileMenu}
                className={`p-2 focus:outline-none transition-colors duration-300 ${currentTextColor}`}
                aria-label="Toggle mobile menu"
              >
                <div className="w-6 h-5 flex flex-col justify-between items-end">
                  <span
                    className={`block h-0.5 rounded-full transition-all duration-300 ${
                      isTransparentState ? 'bg-white' : 'bg-gray-900'
                    } ${isMobileMenuOpen ? 'w-6 rotate-45 translate-y-2.5' : 'w-6'}`}
                  />
                  <span
                    className={`block h-0.5 rounded-full transition-all duration-300 ${
                      isTransparentState ? 'bg-white' : 'bg-gray-900'
                    } ${isMobileMenuOpen ? 'opacity-0' : 'w-4'}`}
                  />
                  <span
                    className={`block h-0.5 rounded-full transition-all duration-300 ${
                      isTransparentState ? 'bg-white' : 'bg-gray-900'
                    } ${isMobileMenuOpen ? 'w-6 -rotate-45 -translate-y-2' : 'w-6'}`}
                  />
                </div>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* 모바일 전체 화면 메뉴 오버레이 */}
      <div
        className={`fixed inset-0 z-40 bg-white transition-all duration-500 ease-in-out lg:hidden ${
          isMobileMenuOpen
            ? 'opacity-100 visible translate-y-0'
            : 'opacity-0 invisible -translate-y-10'
        }`}
        style={{ paddingTop: '80px' }} // 헤더 높이만큼 띄움
      >
        <div className="flex flex-col h-full px-6 pb-10 overflow-y-auto">
          {/* 메뉴 링크 리스트 */}
          <div className="flex flex-col space-y-6 mt-8">
            {[
              { name: 'Home', href: '/', subtitle: '메인으로' },
              { name: 'Company', href: '/company', subtitle: '회사 소개' },
              {
                name: 'Technologies',
                href: '/technologies',
                subtitle: '핵심 보유 기술',
              },
              { name: 'Investor Relations', href: '/board', subtitle: '투자정보' },
              { name: 'Contact Us', href: '/contactus', subtitle: '문의하기' },
            ].map((item, idx) => (
              <Link
                key={item.name}
                href={item.href}
                onClick={() => setIsMobileMenuOpen(false)}
                className={`group flex items-center justify-between py-3 border-b border-gray-100 transition-all duration-500 delay-[${idx * 100}ms] ${
                  isMobileMenuOpen
                    ? 'opacity-100 translate-x-0'
                    : 'opacity-0 -translate-x-10'
                }`}
              >
                <div>
                  <span className="block text-2xl font-bold text-gray-900 group-hover:text-blue-600 transition-colors">
                    {item.name}
                  </span>
                  <span className="text-sm text-gray-500 font-medium mt-1">
                    {item.subtitle}
                  </span>
                </div>
                <span className="text-gray-300 group-hover:text-blue-600 transition-colors text-2xl">
                  →
                </span>
              </Link>
            ))}
          </div>

          {/* 모바일 메뉴 하단 푸터 (연락처 등) */}
          <div
            className={`mt-auto pt-10 transition-all duration-700 delay-300 ${
              isMobileMenuOpen
                ? 'opacity-100 translate-y-0'
                : 'opacity-0 translate-y-10'
            }`}
          >
            <div className="text-gray-600 space-y-1 text-sm">
              <p> j_kim@kddarts.co.kr</p>
              <p> 031-607-3202</p>
            </div>
            <p className="text-gray-300 text-xs mt-6">
              Copyright ©2021 KD darts Co., LTD. All rights reserved
            </p>
          </div>
        </div>
      </div>
    </>
  );
}
